import { isNullOrUndefined, extend, addClass, removeClass } from '@syncfusion/ej2-base';
import { attributes as addAttributes } from '@syncfusion/ej2-base';
import { rowDataBound, queryCellInfo } from '../base/constant';
import { setStyleAndAttributes, getObject, extendObjWithFn, applyStickyLeftRightPosition, groupCaptionRowLeftRightPos, resetColspanGroupCaption, resetColandRowSpanStickyPosition, addFixedColumnBorder } from '../base/util';
import { CellType } from '../base/enum';
import { CellMergeRender } from './cell-merge-renderer';
import * as literals from '../base/string-literals';
/**
 * RowRenderer class which responsible for building row content.
 *
 * @hidden
 */
export class RowRenderer {
    constructor(serviceLocator, cellType, parent) {
        this.isSpan = false;
        this.cellType = cellType;
        this.serviceLocator = serviceLocator;
        this.parent = parent;
        this.element = this.parent.createElement('tr', { attrs: { role: 'row' } });
    }
    /* eslint-disable */
    /**
     * Function to render the row content based on Column[] and data.
     *
     * @param {Row<T>} row - specifies the row
     * @param {Column[]} columns - specifies the columns
     * @param {Object} attributes - specifies the attributes
     * @param {string} rowTemplate - specifies the rowTemplate
     * @param {Element} cloneNode - specifies the cloneNode
     * @returns {Element} returns the element
     */
    /* eslint-enable */
    render(row, columns, attributes, rowTemplate, cloneNode) {
        return this.refreshRow(row, columns, attributes, rowTemplate, cloneNode);
    }
    /* eslint-disable */
    /**
     * Function to refresh the row content based on Column[] and data.
     *
     * @param {Row<T>} row - specifies the row
     * @param {Column[]} columns - specifies the column
     * @param {boolean} isChanged - specifies isChanged
     * @param {Object} attributes - specifies the attributes
     * @param {string} rowTemplate - specifies the rowTemplate
     * @returns {void}
     */
    /* eslint-enable */
    refresh(row, columns, isChanged, attributes, rowTemplate) {
        if (isChanged) {
            row.data = extendObjWithFn({}, row.changes);
            this.refreshMergeCells(row);
        }
        const node = this.parent.element.querySelector('[data-uid=' + row.uid + ']');
        const tr = this.refreshRow(row, columns, attributes, rowTemplate, null, isChanged);
        if (node.classList.contains('e-grid-pin-row')) {
            tr.classList.add('e-grid-pin-row');
        }
        if (isChanged) {
            addFixedColumnBorder(tr);
        }
        const cells = [].slice.call(tr.cells);
        const tempCells = [].slice.call(node.querySelectorAll('.e-templatecell'));
        if (this.parent.isReact && tempCells.length) {
            for (const col of columns) {
                if (col.template) {
                    setTimeout(() => {
                        this.parent.refreshReactColumnTemplateByUid(col.uid, true);
                    }, 0);
                    break;
                }
            }
        }
        const attr = [].slice.call(tr.attributes);
        attr.map((item) => {
            node.setAttribute(item['name'], item['value']);
        });
        node.innerHTML = '';
        for (const cell of cells) {
            node.appendChild(cell);
        }
    }
    // tslint:disable-next-line:max-func-body-length
    refreshRow(row, columns, attributes, rowTemplate, cloneNode, isEdit) {
        const tr = !isNullOrUndefined(cloneNode) ? cloneNode : this.element.cloneNode();
        const rowArgs = { data: row.data };
        const cellArgs = { data: row.data };
        const chekBoxEnable = this.parent.getColumns().filter((col) => col.type === 'checkbox' && col.field)[0];
        let value = false;
        const isFrozen = this.parent.isFrozenGrid();
        let isFirstVisibleCell = true;
        if (chekBoxEnable) {
            value = getObject(chekBoxEnable.field, rowArgs.data);
        }
        const selIndex = this.parent.getSelectedRowIndexes();
        if (row.isDataRow) {
            row.isSelected = selIndex.indexOf(row.index) > -1 || value;
        }
        let currentViewData = this.parent.currentViewData;
        if (row.isDataRow && this.parent.isCheckBoxSelection
            && this.parent.checkAllRows === 'Check' && (this.parent.enableVirtualization || this.parent.enableInfiniteScrolling)) {
            row.isSelected = true;
            if (selIndex.indexOf(row.index) === -1) {
                selIndex.push(row.index);
            }
        }
        this.buildAttributeFromRow(tr, row);
        addAttributes(tr, extend({}, attributes, {}));
        setStyleAndAttributes(tr, row.attributes);
        const cellRendererFact = this.serviceLocator.getService('cellRendererFactory');
        for (let i = 0, len = row.cells.length; i < len; i++) {
            const cell = row.cells[parseInt(i.toString(), 10)];
            cell.isSelected = row.isSelected;
            cell.isColumnSelected = cell.column.isSelected;
            const cellRenderer = cellRendererFact.getCellRenderer(row.cells[parseInt(i.toString(), 10)].cellType
                || CellType.Data);
            const attrs = { 'data-index': !isNullOrUndefined(row.index) ? row.index.toString() : '' };
            if (row.isExpand && row.cells[parseInt(i.toString(), 10)].cellType === CellType.DetailExpand) {
                attrs['class'] = this.parent.isPrinting ? 'e-detailrowcollapse' : 'e-detailrowexpand';
            }
            const isFirstCell = !this.parent.enableRtl && isFirstVisibleCell &&
                (this.parent.gridLines === 'Vertical' || this.parent.gridLines === 'Both');
            let td = cellRenderer.render(row.cells[parseInt(i.toString(), 10)], row.data, attrs, row.isExpand, isEdit);
            if (row.cells[parseInt(i.toString(), 10)].cellType !== CellType.Filter) {
                if (row.cells[parseInt(i.toString(), 10)].cellType === CellType.Data
                    || row.cells[parseInt(i.toString(), 10)].cellType === CellType.CommandColumn) {
                    if (cell.visible && isFirstCell) {
                        if (this.parent.groupSettings && this.parent.groupSettings.columns.length) {
                            td.classList.add('e-grid-group-first-cell');
                        }
                        else if ((this.parent.renderModule && this.parent.renderModule.isFirstColumnHidden) &&
                            !(this.parent.allowRowDragAndDrop || this.parent.childGrid || this.parent.detailTemplate)) {
                            td.classList.add('e-first-visible-cell');
                        }
                        isFirstVisibleCell = false;
                    }
                    const isReactChild = this.parent.parentDetails && this.parent.parentDetails.parentInstObj &&
                        this.parent.parentDetails.parentInstObj.isReact;
                    const isReactPrintGrid = this.parent.printGridParent && this.parent.printGridParent.isReact;
                    if (((this.parent.isReact && this.parent.requireTemplateRef) || (isReactChild &&
                        this.parent.parentDetails.parentInstObj.requireTemplateRef) || (isReactPrintGrid
                        && this.parent.printGridParent.requireTemplateRef)) && cell.isTemplate) {
                        // eslint-disable-next-line @typescript-eslint/no-this-alias
                        const thisRef = this;
                        thisRef.parent.renderTemplates(function () {
                            if (typeof (cell.column.template) !== 'string') {
                                const ariaAttr = td.getAttribute('aria-label');
                                td.setAttribute('aria-label', td.innerText + ariaAttr);
                            }
                            thisRef.parent.trigger(queryCellInfo, extend(cellArgs, {
                                cell: td, column: cell.column, colSpan: 1,
                                rowSpan: 1, foreignKeyData: row.cells[parseInt(i.toString(), 10)].foreignKeyData,
                                requestType: thisRef.parent.requestTypeAction
                            }));
                        });
                    }
                    else {
                        this.parent.trigger(queryCellInfo, extend(cellArgs, {
                            cell: td, column: cell.column, colSpan: 1,
                            rowSpan: 1, foreignKeyData: row.cells[parseInt(i.toString(), 10)].foreignKeyData,
                            requestType: this.parent.requestTypeAction
                        }));
                    }
                    const isNotSpanable = this.parent.enableVirtualization || this.parent.enableColumnVirtualization
                        || this.parent.enableInfiniteScrolling || (this.parent.allowGrouping
                        && this.parent.groupSettings.columns.length && this.parent.groupSettings.enableLazyLoading)
                        || row.index < this.parent.pinnedTopRecords.length;
                    if (!isNotSpanable) {
                        const field = 'field';
                        const dateTypes = ['date', 'datetime', 'dateonly'];
                        if (row.isDataRow && this.parent.enableColumnSpan && cell.column.enableColumnSpan
                            && !cell.isSpanned && cell.visible && !cell.isTemplate && row.data[cell.column[`${field}`]] !== '' && !cell.isForeignKey) {
                            let colspan = 1;
                            let cellValue = cellRenderer.getValue(cell.column[`${field}`], row.data, cell.column);
                            if (dateTypes.indexOf(cell.column.type) !== -1 && !isNullOrUndefined(cellValue)
                                && cellValue instanceof Date) {
                                cellValue = cellValue.getTime();
                            }
                            for (let j = i; j < row.cells.length - 1; j++) {
                                const nextCell = row.cells[parseInt(j.toString(), 10) + 1];
                                let nextCellValue = cellRenderer.getValue(nextCell.column[`${field}`], row.data, nextCell.column);
                                if (dateTypes.indexOf(nextCell.column.type) !== -1 && !isNullOrUndefined(nextCellValue)
                                    && nextCellValue instanceof Date) {
                                    nextCellValue = nextCellValue.getTime();
                                }
                                if (cellValue === nextCellValue && nextCell.visible
                                    && nextCell.column.enableColumnSpan && !nextCell.isForeignKey) {
                                    colspan++;
                                }
                                else {
                                    break;
                                }
                            }
                            cellArgs.colSpan = colspan;
                        }
                        if (row.isDataRow && this.parent.enableRowSpan && cell.column.enableRowSpan
                            && !cell.isSpanned && !cell.isTemplate && cell.visible && row.data[cell.column[`${field}`]] !== '' && !cell.isForeignKey) {
                            const index = row.index - this.parent.pinnedTopRecords.length;
                            const nxtRowIndex = this.parent.groupSettings.columns.length ? row.groupDataIndex : index;
                            if (row.isDataRow && this.parent.groupSettings.columns.length && !isNullOrUndefined(row.parentUid)) {
                                currentViewData = this.parent.getRowObjectFromUID(row.parentUid).data.items;
                            }
                            let rowspan = 1;
                            let cellValue = cellRenderer.getValue(cell.column[`${field}`], row.data, cell.column);
                            if (dateTypes.indexOf(cell.column.type) !== -1 && !isNullOrUndefined(cellValue)
                                && cellValue instanceof Date) {
                                cellValue = cellValue.getTime();
                            }
                            for (let j = nxtRowIndex + 1; j < currentViewData.length; j++) {
                                let nextRowCellValue = cellRenderer.getValue(cell.column[`${field}`], currentViewData[parseInt(j.toString(), 10)], cell.column);
                                if (dateTypes.indexOf(cell.column.type) !== -1 && !isNullOrUndefined(nextRowCellValue)
                                    && nextRowCellValue instanceof Date) {
                                    nextRowCellValue = nextRowCellValue.getTime();
                                }
                                if (cellValue === nextRowCellValue) {
                                    rowspan++;
                                }
                                else {
                                    break;
                                }
                            }
                            cellArgs.rowSpan = rowspan;
                        }
                    }
                    let isRowSpanned = false;
                    if (row.index > this.parent.pinnedTopRecords.length && (this.isSpan || (this.parent.isSpan && isEdit))) {
                        const index = row.index - this.parent.pinnedTopRecords.length;
                        const rowsObject = this.parent.getRowsObject().filter((row) => row.isDataRow);
                        const prevRowCells = this.parent.groupSettings.columns.length > 0
                            && !rowsObject[index - 1].isDataRow ? rowsObject[parseInt(index.toString(), 10)].cells
                            : rowsObject[parseInt(index.toString(), 10) - 1].cells;
                        const uid = 'uid';
                        const prevRowCell = prevRowCells.filter((cell) => cell.column.uid === row.cells[parseInt(i.toString(), 10)].column[`${uid}`])[0];
                        isRowSpanned = prevRowCell.isRowSpanned ? prevRowCell.isRowSpanned : prevRowCell.rowSpanRange > 1;
                    }
                    if ((cellArgs.rowSpan > 1 || cellArgs.colSpan > 1) && row.index >= this.parent.pinnedTopRecords.length) {
                        const index = row.index - this.parent.pinnedTopRecords.length;
                        this.resetrowSpanvalue(this.parent.frozenRows > index ? this.parent.frozenRows :
                            currentViewData.length, cellArgs, index);
                        if (cellArgs.column.visible === false) {
                            cellArgs.colSpan = 1;
                        }
                        else {
                            if (isFrozen) {
                                const columns = this.parent.getColumns();
                                const right = this.parent.getFrozenRightColumnsCount();
                                const left = this.parent.getFrozenLeftCount();
                                const movableCount = columns.length - right;
                                const cellIdx = cellArgs.column.index;
                                if (left > cellIdx && left < (cellIdx + cellArgs.colSpan)) {
                                    const colSpan = (cellIdx + cellArgs.colSpan) - left;
                                    cellArgs.colSpan = cellArgs.colSpan - colSpan;
                                }
                                else if (movableCount <= cellIdx && columns.length < (cellIdx + cellArgs.colSpan)) {
                                    const colSpan = (cellIdx + cellArgs.colSpan) - columns.length;
                                    cellArgs.colSpan = cellArgs.colSpan - colSpan;
                                }
                                else if (cellArgs.column.freeze === 'Fixed') {
                                    let colSpan = 1;
                                    const index = cellIdx;
                                    for (let j = index + 1; j < index + cellArgs.colSpan; j++) {
                                        if (columns[parseInt(j.toString(), 10)].freeze === 'Fixed') {
                                            colSpan++;
                                        }
                                        else {
                                            break;
                                        }
                                    }
                                    cellArgs.colSpan = colSpan;
                                }
                                else if (movableCount > cellIdx && movableCount < (cellIdx + cellArgs.colSpan)) {
                                    const colSpan = (cellIdx + cellArgs.colSpan) - movableCount;
                                    cellArgs.colSpan = cellArgs.colSpan - colSpan;
                                }
                            }
                        }
                    }
                    if ((cellArgs.colSpan > 1 || row.cells[parseInt(i.toString(), 10)].cellSpan > 1 || cellArgs.rowSpan > 1
                        || isRowSpanned) && row.index >= this.parent.pinnedTopRecords.length) {
                        this.parent.isSpan = true;
                        this.isSpan = true;
                        const cellMerge = new CellMergeRender(this.serviceLocator, this.parent);
                        td = cellMerge.render(cellArgs, row, i, td);
                        if (isFrozen) {
                            resetColandRowSpanStickyPosition(this.parent, cellArgs.column, td, cellArgs.colSpan);
                        }
                    }
                }
                if ((cell.cellType === CellType.Header || cell.cellType === CellType.StackedHeader) &&
                    isFirstCell && (cell.visible || cell.cellType === CellType.StackedHeader)) {
                    const visibleColumns = this.parent.getVisibleColumns();
                    const field = 'field';
                    const type = 'type';
                    if ((cell.column[`${type}`] && cell.column[`${type}`] === 'checkbox') ||
                        (cell.cellType === CellType.Header && cell.column[`${field}`] && visibleColumns.length &&
                            visibleColumns[0].field === cell.column[`${field}`]) || cell.cellType === CellType.StackedHeader) {
                        if (this.parent.groupSettings && this.parent.groupSettings.columns.length) {
                            td.classList.add('e-grid-group-first-cell');
                        }
                        else if ((this.parent.renderModule && this.parent.renderModule.isFirstColumnHidden) &&
                            !(this.parent.allowRowDragAndDrop || this.parent.childGrid || this.parent.detailTemplate)) {
                            td.classList.add('e-first-visible-cell');
                        }
                    }
                    isFirstVisibleCell = false;
                }
                if (cell.cellType === CellType.Header && (row.cells[parseInt(i.toString(), 10)].colSpan > 1 ||
                    row.cells[parseInt(i.toString(), 10)].rowSpan > 1)) {
                    const cellMerge = new CellMergeRender(this.serviceLocator, this.parent);
                    td = cellMerge.render(row.cells[parseInt(i.toString(), 10)], row, i, td);
                }
                if (this.isSpan) {
                    const rowsObject = this.parent.getRowsObject();
                    const isRtl = this.parent.enableRtl;
                    const index = row.index - this.parent.pinnedTopRecords.length;
                    if (rowsObject[row.index - 1] && rowsObject[index - 1].isDataRow) {
                        const prevRowCells = rowsObject[index - 1].cells;
                        const prevRowCell = prevRowCells[i - 1];
                        const currentRowCell = prevRowCells[parseInt(i.toString(), 10)];
                        const nextRowCell = prevRowCells[i + 1];
                        const direction = prevRowCells[parseInt(i.toString(), 10)].column.freeze;
                        if (prevRowCell && (prevRowCell.isRowSpanned || prevRowCell.rowSpanRange > 1) && prevRowCell.visible) {
                            if (!isRtl && (!currentRowCell.isRowSpanned || prevRowCell.rowSpanRange > currentRowCell.rowSpanRange)) {
                                td.classList.add('e-rowcell-firstchild');
                            }
                            if (isFrozen) {
                                if (prevRowCell.column.freeze === 'Fixed' && direction === 'Fixed') {
                                    td.classList.add(isRtl ? 'e-removefreezerightborder' : 'e-removefreezeleftborder');
                                }
                                else if (!isRtl && i === 1 && direction === 'Left') {
                                    td.classList.add('e-addfreezefirstchildborder');
                                }
                            }
                        }
                        if (nextRowCell && (nextRowCell.isRowSpanned || nextRowCell.rowSpanRange > 1) && nextRowCell.visible) {
                            if (isRtl && (!currentRowCell.isRowSpanned || nextRowCell.rowSpanRange > currentRowCell.rowSpanRange)) {
                                td.classList.add('e-rowcell-lastchild');
                            }
                            if (isFrozen && nextRowCell.column.freeze === 'Fixed' && direction === 'Fixed' && cellArgs.colSpan < 2) {
                                td.classList.add(isRtl ? 'e-removefreezeleftborder' : 'e-removefreezerightborder');
                            }
                        }
                    }
                }
                if (cellArgs.rowSpan > 1
                    && currentViewData.length - (row.index - this.parent.pinnedTopRecords.length) === cellArgs.rowSpan) {
                    td.classList.add('e-row-span-lastrowcell');
                }
                if (!row.cells[parseInt(i.toString(), 10)].isSpanned) {
                    tr.appendChild(td);
                }
            }
            else {
                if (cell.visible && isFirstCell) {
                    if (this.parent.groupSettings && this.parent.groupSettings.columns.length) {
                        td.classList.add('e-grid-group-first-cell');
                    }
                    else if ((this.parent.renderModule && this.parent.renderModule.isFirstColumnHidden) &&
                        !(this.parent.allowRowDragAndDrop || this.parent.childGrid || this.parent.detailTemplate)) {
                        td.classList.add('e-first-visible-cell');
                    }
                    isFirstVisibleCell = false;
                }
            }
        }
        let emptyColspan = 0;
        if (this.parent.groupSettings.columns.length && this.parent.getFrozenLeftColumnsCount()) {
            if (tr.classList.contains('e-groupcaptionrow')) {
                const freezeCells = [].slice.call(tr.querySelectorAll('.e-leftfreeze,.e-unfreeze,.e-rightfreeze,.e-fixedfreeze,.e-freezerightborder,.e-freezeleftborder'));
                if (freezeCells.length) {
                    removeClass(freezeCells, ['e-leftfreeze', 'e-unfreeze', 'e-rightfreeze', 'e-fixedfreeze', 'e-freezerightborder', 'e-freezeleftborder']);
                }
                if (tr.querySelector('.e-summarycell')) {
                    groupCaptionRowLeftRightPos(tr, this.parent);
                }
                else {
                    for (let j = 0; j < tr.childNodes.length; j++) {
                        const td = tr.childNodes[parseInt(j.toString(), 10)];
                        td.classList.add('e-leftfreeze');
                        applyStickyLeftRightPosition(td, j * 30, this.parent.enableRtl, 'Left');
                        if (td.classList.contains('e-groupcaption')) {
                            const oldColspan = parseInt(td.getAttribute('colspan'), 10);
                            const colspan = resetColspanGroupCaption(this.parent, j);
                            td.setAttribute('colspan', colspan.toString());
                            emptyColspan = oldColspan - colspan;
                        }
                    }
                    if (emptyColspan) {
                        const td = this.parent.createElement('TD', {
                            className: 'e-groupcaption',
                            attrs: { colspan: emptyColspan.toString(), id: this.parent.element.id + 'captioncell', tabindex: '-1' }
                        });
                        tr.appendChild(td);
                    }
                }
            }
            if ((tr.querySelectorAll('.e-leftfreeze').length || tr.classList.contains('e-columnheader')) &&
                (tr.querySelectorAll('.e-indentcell').length || tr.querySelectorAll('.e-grouptopleftcell').length)) {
                const td = tr.querySelectorAll('.e-indentcell, .e-grouptopleftcell');
                for (let i = 0; i < td.length; i++) {
                    td[parseInt(i.toString(), 10)].classList.add('e-leftfreeze');
                    applyStickyLeftRightPosition(td[parseInt(i.toString(), 10)], i * 30, this.parent.enableRtl, 'Left');
                }
            }
        }
        const args = { row: tr, rowHeight: this.parent.rowHeight };
        if (row.isDataRow) {
            const eventArg = extend(rowArgs, args);
            eventArg.isSelectable = true;
            const primaryKey = this.parent.getPrimaryKeyFieldNames()[0];
            const isPrimaryKeyValue = row.data[`${primaryKey}`];
            if (this.parent.renderModule && Object.keys(this.parent.renderModule.nonselectableDataKey).length &&
                this.parent.renderModule.nonselectableDataKey[`${isPrimaryKeyValue}`] === false) {
                eventArg.isSelectable = false;
            }
            const isReactChild = this.parent.parentDetails && this.parent.parentDetails.parentInstObj &&
                this.parent.parentDetails.parentInstObj.isReact;
            const cellTemplate = eventArg.row.querySelectorAll('.e-templatecell');
            if (((this.parent.isReact && this.parent.requireTemplateRef) || (isReactChild &&
                this.parent.parentDetails.parentInstObj.requireTemplateRef)) && cellTemplate.length) {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const thisRef = this;
                thisRef.parent.renderTemplates(function () {
                    thisRef.parent.trigger(rowDataBound, eventArg);
                    if (!eventArg.isSelectable) {
                        row.isSelectable = eventArg.isSelectable;
                        thisRef.disableRowSelection(thisRef, row, args, eventArg);
                    }
                });
            }
            else {
                this.parent.trigger(rowDataBound, eventArg);
            }
            row.isSelectable = eventArg.isSelectable;
            const isDraggable = this.parent.isRowDragable();
            if (this.parent.allowPaging && this.parent.selectionSettings.persistSelection) {
                const pKey = row.data ? row.data[`${primaryKey}`] : null;
                const SelectedRecords = eventArg.isSelectable ? this.parent.partialSelectedRecords :
                    this.parent.disableSelectedRecords;
                if (!SelectedRecords.some((data) => data[`${primaryKey}`] === pKey)) {
                    SelectedRecords.push(row.data);
                }
            }
            if (!eventArg.isSelectable) {
                this.disableRowSelection(this, row, args, eventArg);
            }
            if (this.parent.childGrid || isDraggable || this.parent.detailTemplate) {
                const td = tr.querySelectorAll('.e-rowcell:not(.e-hide)')[0];
                if (td) {
                    td.classList.add('e-detailrowvisible');
                }
            }
        }
        if (this.parent.enableVirtualization) {
            rowArgs.rowHeight = this.parent.rowHeight;
        }
        if (rowArgs.rowHeight) {
            tr.style.height = rowArgs.rowHeight + 'px';
        }
        else if (this.parent.rowHeight && (tr.querySelector('.e-headercell') || tr.querySelector('.e-groupcaption'))) {
            tr.style.height = this.parent.rowHeight + 'px';
        }
        if (row.cssClass) {
            tr.classList.add(row.cssClass);
        }
        if (row.lazyLoadCssClass) {
            tr.classList.add(row.lazyLoadCssClass);
        }
        if (this.parent.rowRenderingMode === 'Vertical' && this.parent.allowTextWrap && (this.parent.textWrapSettings.wrapMode === 'Header'
            || this.parent.textWrapSettings.wrapMode === 'Both')) {
            tr.classList.add('e-verticalwrap');
        }
        const vFTable = this.parent.enableColumnVirtualization;
        if (!vFTable && this.parent.aggregates.length && this.parent.element.scrollHeight > this.parent.height) {
            for (let i = 0; i < this.parent.aggregates.length; i++) {
                const property = 'properties';
                const column = 'columns';
                if (this.parent.aggregates[parseInt(i.toString(), 10)][`${property}`][`${column}`][0].footerTemplate) {
                    const summarycell = [].slice.call(tr.getElementsByClassName('e-summarycell'));
                    if (summarycell.length) {
                        const lastSummaryCell = (summarycell[summarycell.length - 1]);
                        addClass([lastSummaryCell], ['e-lastsummarycell']);
                        const firstSummaryCell = (summarycell[0]);
                        addClass([firstSummaryCell], ['e-firstsummarycell']);
                    }
                }
            }
        }
        return tr;
    }
    resetrowSpanvalue(rowCount, cellArgs, rowIndex) {
        if (rowCount > rowIndex && rowCount < rowIndex + cellArgs.rowSpan) {
            const rowSpan = (rowIndex + cellArgs.rowSpan) - rowCount;
            cellArgs.rowSpan = cellArgs.rowSpan - rowSpan;
        }
    }
    disableRowSelection(thisRef, row, args, eventArg) {
        const selIndex = this.parent.getSelectedRowIndexes();
        this.parent.selectionModule.isPartialSelection = true;
        row.isSelected = false;
        const selRowIndex = selIndex.indexOf(row.index);
        if (selRowIndex > -1) {
            selIndex.splice(selRowIndex, 1);
        }
        const chkBox = args.row.querySelectorAll('.e-rowcell.e-gridchkbox');
        const isDrag = eventArg.row.querySelector('.e-rowdragdrop');
        const cellIdx = thisRef.parent.groupSettings.columns.length + (isDrag || thisRef.parent.isDetail() ? 1 : 0);
        for (let i = 0; i < chkBox.length; i++) {
            chkBox[parseInt(i.toString(), 10)].firstElementChild.classList.add('e-checkbox-disabled');
            chkBox[parseInt(i.toString(), 10)].querySelector('.e-frame').classList.remove('e-check');
        }
        if (row.cells.length) {
            for (let i = cellIdx; i < row.cells.length; i++) {
                const cell = eventArg.row.querySelector('.e-rowcell[aria-colindex="' + (row.cells[parseInt(i.toString(), 10)].index + 1) + '"]');
                if (cell) {
                    removeClass([cell], ['e-selectionbackground', 'e-active']);
                }
            }
        }
        if (isDrag) {
            removeClass([isDrag], ['e-selectionbackground', 'e-active']);
        }
    }
    refreshMergeCells(row) {
        for (const cell of row.cells) {
            cell.isSpanned = false;
        }
        return row;
    }
    /* eslint-disable */
    /**
     * Function to check and add alternative row css class.
     *
     * @param {Element} tr - specifies the tr element
     * @param {Row<T>} row - specifies the row
     * @returns {void}
     */
    /* eslint-enable */
    buildAttributeFromRow(tr, row) {
        const attr = {};
        const prop = { 'dataUID': 'data-uid', 'ariaSelected': 'aria-selected' };
        const classes = [];
        if (row.isDataRow) {
            classes.push(literals.row);
        }
        if (row.isAltRow) {
            classes.push('e-altrow');
        }
        if (row.isCaptionRow) {
            classes.push('e-groupcaptionrow');
        }
        if (row.isAggregateRow && row.parentUid) {
            classes.push('e-groupfooterrow');
        }
        if (!isNullOrUndefined(row.index)) {
            attr[literals.ariaRowIndex] = row.index + 1;
        }
        if (row.rowSpan) {
            attr.rowSpan = row.rowSpan;
        }
        if (row.uid) {
            attr[prop.dataUID] = row.uid;
        }
        if (row.isSelected) {
            attr[prop.ariaSelected] = true;
        }
        if (row.visible === false) {
            classes.push('e-hide');
        }
        attr.class = classes;
        setStyleAndAttributes(tr, attr);
    }
}
